/**
 * Ending a production (terminal: it takes no more commands). An active one
 * must be deactivated first — the server refuses (`conflict`) so nothing on
 * air ends by accident, and the dialog says so up front. The reason goes to
 * the as-run log.
 */
import { type FormEvent, useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';

/** The contract's limit on an end reason (`endProductionRequestSchema`). */
export const END_REASON_MAX = 500;

export type EndProductionDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly label: string;
  /** Its playlist is activated: ending will be refused until it is deactivated. */
  readonly active: boolean;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (reason: string | null) => void;
};

function EndForm({ onOpenChange, label, active, pending, error, onSubmit }: Omit<EndProductionDialogProps, 'open'>) {
  const [reason, setReason] = useState('');
  const tooLong = reason.trim().length > END_REASON_MAX;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (tooLong || pending) return;
    onSubmit(reason.trim() === '' ? null : reason.trim());
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>End production?</DialogTitle>
        <DialogDescription>
          “{label}” takes no more commands once ended. This cannot be undone.
          {active ? ' It is active: deactivate it first — an active production is not ended.' : ''}
        </DialogDescription>
      </DialogHeader>
      <Field invalid={tooLong}>
        <Label htmlFor="end-reason">Reason (for the as-run log)</Label>
        <Input id="end-reason" value={reason} onChange={(e) => setReason(e.target.value)} disabled={pending} aria-invalid={tooLong} />
        <FieldError>{tooLong ? `At most ${String(END_REASON_MAX)} characters.` : error}</FieldError>
      </Field>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" variant="destructive" disabled={tooLong || pending} data-testid="end-production-submit">
          {pending ? 'Ending…' : 'End production'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function EndProductionDialog({ open, ...rest }: EndProductionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={rest.onOpenChange}>
      <DialogContent data-testid="end-production-dialog">
        <EndForm {...rest} />
      </DialogContent>
    </Dialog>
  );
}
